import { AppIcon } from "~/components/app-icon";
import type { WorkbenchSource } from "~/lib/constants/workbench";
import * as m from "~/paraglide/messages.js";
import type { Decision } from "./use-workbench";

interface InspectorProps {
  decision: Decision;
  description: string;
  label: string;
  needsPerson: boolean;
  onApprove: () => void;
  onHold: () => void;
  sources: WorkbenchSource[];
}

export function Inspector({
  decision,
  description,
  label,
  needsPerson,
  onApprove,
  onHold,
  sources,
}: Readonly<InspectorProps>) {
  const settled = decision !== "open";

  const approveLabel = () => {
    if (decision === "approved") {
      return needsPerson ? m.wb_flagged_action() : m.wb_approved_action();
    }
    return needsPerson ? m.wb_action_flag() : m.wb_action_approve();
  };

  const holdLabel = () => {
    if (decision === "held") {
      return m.wb_hold_done();
    }
    return needsPerson ? m.wb_hold_operations() : m.wb_hold_draft();
  };

  return (
    <aside aria-label={m.home_bench_inspector_label()} className="inspector">
      <h4>{m.home_bench_sources_title()}</h4>

      <div id="source-list">
        {sources.map((source) => (
          <div className="source" key={source.name}>
            <AppIcon name={source.icon} />
            <div>
              <strong>{source.name}</strong>
              <span>{source.detail}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="approval-box">
        <span className="approval-label">{label}</span>
        <p>{description}</p>

        {/* `aria-disabled` rather than `disabled`: the design disables the button
            the visitor just pressed, which drops focus to <body> and orphans the
            status announcement. This keeps focus where it is. */}
        <button
          aria-disabled={settled}
          className="button button-solid approve"
          onClick={settled ? undefined : onApprove}
          type="button"
        >
          {approveLabel()}{" "}
          {decision === "approved" ? null : <span aria-hidden="true">✓</span>}
        </button>

        <button
          aria-disabled={settled}
          className="hold"
          onClick={settled ? undefined : onHold}
          type="button"
        >
          {holdLabel()}
        </button>
      </div>
    </aside>
  );
}
